import { useEffect, useMemo, useRef, useState } from "react";
import { Smartphone, Tablet, Monitor, X, Maximize2 } from "lucide-react";

/**
 * Realistični device frame okoli iframe-a / screenshot-a z aplikacijo.
 * - mobile: 390×780 (iPhone-ish), notch
 * - tablet: 820×1100 (iPad Air), tanek okvir
 * - desktop: 1440×900, laptop base
 */
const FRAMES = {
  mobile: { w: 390, h: 780, label: "Mobilni" },
  tablet: { w: 820, h: 1100, label: "Tablica" },
  desktop: { w: 1440, h: 900, label: "Računalnik" },
};

// Zaznaj trenutno napravo (grobo) za privzete izbire.
export function detectCurrentDevice() {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  const hasFinePointer = window.matchMedia?.("(hover: hover) and (pointer: fine)")?.matches;
  if (w < 768) return "mobile";
  if (w < 1180 || !hasFinePointer) return "tablet";
  return "desktop";
}

export function DeviceSwitcherToolbar({ mode, onModeChange, onClose, title = "Predogled na", available = ["mobile", "tablet", "desktop"] }) {
  const btn = (m, Icon, label) => {
    if (!available.includes(m)) return null;
    return (
      <button
        onClick={() => onModeChange(m)}
        data-testid={`device-mode-${m}`}
        className={`p-2 rounded-full transition-all ${
          mode === m
            ? "bg-white text-slate-900 shadow"
            : "text-white/80 hover:text-white hover:bg-white/10"
        }`}
        title={label}
        aria-label={label}
      >
        <Icon className="w-4 h-4" />
      </button>
    );
  };

  return (
    <div className="inline-flex items-center gap-1 bg-slate-800/95 backdrop-blur border border-white/10 rounded-full px-2 py-1.5 text-white shadow-lg" data-testid="device-switcher-toolbar">
      <span className="text-xs text-white/70 pl-2 pr-1">{title}</span>
      {btn("mobile", Smartphone, "Mobilni")}
      {btn("tablet", Tablet, "Tablica")}
      {btn("desktop", Monitor, "Računalnik")}
      {onClose && (
        <button
          onClick={onClose}
          className="ml-1 p-1.5 rounded-full text-white/70 hover:text-white hover:bg-white/10"
          data-testid="device-close-btn"
          title="Zapri"
          aria-label="Zapri"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

/**
 * Sam device frame – prikazuje iframe znotraj vizualnega okvira izbrane naprave.
 * Za landing page (velikost prilagojena; scale-down če ni prostora).
 */
const Screen = ({ w, h, radius, src, imageSrc, isolated }) => (
  <div
    className="relative overflow-hidden bg-white"
    style={{ width: w, height: h, borderRadius: radius }}
  >
    {imageSrc ? (
      <img
        src={imageSrc}
        alt="App preview"
        className="w-full h-full object-contain object-top bg-white"
        draggable={false}
      />
    ) : (
      <iframe
        title="App preview"
        src={src}
        className="w-full h-full border-0"
        // "isolated" način: iframe še vedno ima allow-same-origin, da normalno deluje
        // (localStorage, cookies, service worker). Izolacija med parent-om in iframe-om
        // je izvedena PROGRAMSKO prek `TOKEN_KEY = "preview_token"` v App.js (glej IS_IFRAME).
        // Sandbox uporabimo samo za dodatno varnost (blokira top-level navigation itd.).
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-storage-access-by-user-activation"
      />
    )}
  </div>
);

// Skupne "outer" dimenzije okvira, vključno s pointi za device chrome.
function frameOuter(mode) {
  const f = FRAMES[mode] || FRAMES.desktop;
  if (mode === "mobile") return { w: f.w + 24, h: f.h + 24 };
  if (mode === "tablet") return { w: f.w + 32, h: f.h + 32 };
  // desktop: dodaj laptop base
  return { w: f.w + 120, h: f.h + 24 + 16 };
}

export function DeviceFrame({ src, imageSrc, mode = "desktop", fit = "auto", maxHeight = 720, isolated = false }) {
  const f = FRAMES[mode] || FRAMES.desktop;
  const outer = frameOuter(mode);

  const isMobile = mode === "mobile";
  const isTablet = mode === "tablet";
  const isDesktop = mode === "desktop";

  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  // Auto-scale: prilagodi tako, da se cel okvir prilega širini in višini staršev
  useEffect(() => {
    if (fit !== "auto") return;
    const el = containerRef.current;
    if (!el) return;
    const compute = () => {
      const rect = el.getBoundingClientRect();
      const availW = Math.max(200, rect.width - 16);
      const availH = Math.max(200, maxHeight);
      const s = Math.min(1, availW / outer.w, availH / outer.h);
      setScale(s);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    window.addEventListener("resize", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [outer.w, outer.h, fit, maxHeight]);

  const scaledStyle = fit === "auto"
    ? { transform: `scale(${scale})`, transformOrigin: "top center" }
    : {};

  // Rezerviraj potreben prostor (skalirana višina + minimalen padding)
  const reservedHeight = fit === "auto" ? Math.ceil(outer.h * scale) + 8 : outer.h;

  return (
    <div
      ref={containerRef}
      className="flex justify-center items-start w-full overflow-hidden"
      style={{ minHeight: reservedHeight }}
      data-testid={`device-frame-${mode}`}
    >
      <div
        style={scaledStyle}
        className="relative"
      >
        {/* MOBITEL */}
        {isMobile && (
          <div
            className="relative bg-slate-900 rounded-[44px] p-3 shadow-2xl"
            style={{ width: f.w + 24, height: f.h + 24 }}
          >
            {/* notch */}
            <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-6 bg-slate-900 rounded-b-2xl z-20" />
            <Screen w={f.w} h={f.h} radius={32} src={src} imageSrc={imageSrc} isolated={isolated} />
          </div>
        )}

        {/* TABLICA */}
        {isTablet && (
          <div
            className="relative bg-slate-800 rounded-[28px] p-4 shadow-2xl"
            style={{ width: f.w + 32, height: f.h + 32 }}
          >
            <Screen w={f.w} h={f.h} radius={18} src={src} imageSrc={imageSrc} isolated={isolated} />
          </div>
        )}

        {/* RAČUNALNIK */}
        {isDesktop && (
          <div className="flex flex-col items-center">
            <div
              className="relative bg-slate-800 rounded-t-xl p-3 shadow-2xl"
              style={{ width: f.w + 20 }}
            >
              <Screen w={f.w} h={f.h} radius={6} src={src} imageSrc={imageSrc} isolated={isolated} />
            </div>
            {/* laptop base */}
            <div
              className="h-3 bg-slate-700 rounded-b-xl shadow"
              style={{ width: f.w + 120 }}
            />
            <div className="h-1 w-24 bg-slate-500/70 rounded-b-lg" />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Overlay preko celotnega ekrana – uporabno iz demo aplikacije, ko uporabnik klikne "Predogled naprave".
 * Uporablja isti DeviceFrame in DeviceSwitcherToolbar.
 */
export function DevicePreviewOverlay({ open, src, initialMode, onClose, available }) {
  // Če ni podanega začetnega načina, izberi prvo razpoložljivo možnost.
  const defaultMode = initialMode || (available && available[0]) || "mobile";
  const [mode, setMode] = useState(defaultMode);

  // Kadar se odpre in initialMode ni določen, poskrbi da mode ustreza available.
  useEffect(() => {
    if (open && available && !available.includes(mode)) {
      setMode(available[0]);
    }
  }, [open, available, mode]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] bg-slate-950/95 backdrop-blur-sm overflow-auto"
      data-testid="device-preview-overlay"
    >
      <div className="sticky top-0 z-10 flex justify-center py-3 bg-gradient-to-b from-slate-950 to-transparent">
        <DeviceSwitcherToolbar
          mode={mode}
          onModeChange={setMode}
          onClose={onClose}
          title="Predogled na"
          available={available || ["mobile", "tablet", "desktop"]}
        />
      </div>
      <div className="px-4 pb-10 pt-2">
        <DeviceFrame src={src} mode={mode} fit="auto" maxHeight={typeof window !== "undefined" ? window.innerHeight - 100 : 800} />
      </div>
    </div>
  );
}

/**
 * Lebdeč gumb za odpiranje DevicePreviewOverlay (uporabljen v demo-ju).
 * Samodejno zazna trenutno napravo in ponudi le možnosti za DRUGE naprave.
 * Pozicija: bottom-center na mobitelu (bolj dosegljivo s palcem), bottom-right na večjih ekranih.
 */
export function DevicePreviewFAB({ src }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(() => detectCurrentDevice());

  useEffect(() => {
    const handler = () => setCurrent(detectCurrentDevice());
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Ponudi le tiste možnosti, ki niso trenutna naprava.
  const available = useMemo(
    () => ["mobile", "tablet", "desktop"].filter((m) => m !== current),
    [current]
  );

  // Če je uporabnik na mobitelu, ni potrebno ponujati "mobilnega predogleda" – ampak
  // še vedno lahko vidi kako izgleda na tablici ali računalniku.
  if (available.length === 0) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="device-preview-fab"
        className="fixed z-[9997] bg-slate-900 hover:bg-slate-800 text-white rounded-full px-4 py-3 shadow-2xl flex items-center gap-2 border border-white/10
                   bottom-4 left-1/2 -translate-x-1/2
                   md:bottom-5 md:right-5 md:left-auto md:translate-x-0"
        title="Predogled na drugi napravi"
      >
        <Maximize2 className="w-4 h-4" />
        <span className="text-sm font-medium">Predogled na</span>
        {available.includes("mobile") && <Smartphone className="w-4 h-4" title="Mobilni" />}
        {available.includes("tablet") && <Tablet className="w-4 h-4" title="Tablica" />}
        {available.includes("desktop") && <Monitor className="w-4 h-4" title="Računalnik" />}
      </button>
      <DevicePreviewOverlay
        open={open}
        onClose={() => setOpen(false)}
        src={src}
        available={available}
      />
    </>
  );
}
